import React, { useState, useEffect } from 'react';
import './App.css';

// Importaciones modulares
import { useTokens } from './hooks/useTokens';
import { formatAddress, copyToClipboard } from './utils/helpers';

// Sistema de internacionalización
const translations = {
  en: {
    appTitle: "Chessbitcoin Wallet",
    mainnet: "Mainnet",
    login: "Login",
    logout: "Logout",
    temporary: "(Temporary)",
    loginRequired: "You must log in to create a wallet",
    walletCreated: "Wallet created successfully!",
    walletRestored: "Wallet restored successfully!",
    sessionClosed: "Session closed",
    dataUpdated: "Data updated",
    invalidNetworkUSDT: "⚠️ DANGER: You're trying to send Solana USDT to an Ethereum address!",
    invalidNetworkUSDTDetail: "This address is for Ethereum USDT. You have Solana USDT (SPL token).",
    invalidNetworkUSDTAction: "Ask your friend for their Solana USDT address (starts with 'EPjF...' or similar)",
    invalidNetworkGeneric: "⚠️ Invalid network address",
    validSolanaAddress: "✅ Valid Solana address",
    wallet: "💰 Wallet",
    send: "📤 Send",
    swap: "🔄 Swap",
    receive: "📥 Receive",
    wallets: "👛 Wallets",
    totalBalance: "Total Balance",
    temporarySession: "⚠️ Temporary session - Log in to save your wallet",
    updateAll: "🔄 Update All",
    updating: "⏳ Updating...",
    yourTokens: "Your Tokens",
    noTokens: "No tokens with balance found",
    reloadTokens: "🔄 Reload Tokens",
    walletInfo: "Wallet Information",
    address: "Address:",
    copy: "📋 Copy",
    mnemonic: "Mnemonic:",
    showMnemonic: "👁️ Show Mnemonic",
    hideMnemonic: "👁️ Hide Mnemonic",
    sendTokens: "Send Tokens",
    tokenToSend: "Token to Send:",
    recipient: "Recipient:",
    enterSolanaAddress: "Enter Solana address",
    amount: "Amount",
    availableBalance: "Available balance:",
    max: "MAX",
    send: "🚀 Send",
    sending: "⏳ Sending...",
    yourWallets: "Your Wallets",
    temporaryWallets: "Temporary Wallets",
    walletsWillBeLost: "⚠️ Temporary wallets will be lost when reloading the page",
    active: "Active",
    delete: "Delete",
    addNewWallet: "+ Add New Wallet",
    createTemporaryWallet: "+ Create Temporary Wallet",
    manageWallets: "Manage Wallets",
    createTemporaryWalletModal: "Create Temporary Wallet",
    newWallet: "🆕 New Wallet",
    creating: "⏳ Creating...",
    or: "OR",
    enterMnemonic: "Enter your 12 or 24 word mnemonic phrase",
    restoreWallet: "🔑 Restore Wallet",
    restoring: "⏳ Restoring...",
    loginTitle: "Login",
    registerTitle: "Sign Up",
    email: "Email:",
    enterEmail: "your@email.com",
    username: "Username:",
    enterUsername: "Username",
    password: "Password:",
    minPassword: "Minimum 6 characters",
    processing: "⏳ Processing...",
    noAccount: "Don't have an account?",
    haveAccount: "Already have an account?",
    signUpHere: "Sign up here",
    loginHere: "Login here",
    forgotPassword: "Forgot your password?",
    resetPassword: "Reset Password",
    resetPasswordTitle: "Reset Your Password",
    enterEmailForReset: "Enter your email to reset your password",
    sendResetLink: "Send Reset Link",
    resetLinkSent: "Reset link sent to your email!",
    backToLogin: "Back to Login",
    resetPasswordModalTitle: "Reset Your Password",
    newPassword: "New Password:",
    confirmPassword: "Confirm Password:",
    enterNewPassword: "Enter new password",
    reEnterPassword: "Re-enter password",
    passwordsDontMatch: "Passwords don't match",
    resetting: "⏳ Resetting...",
    resetPasswordBtn: "Reset Password",
    passwordChanged: "Password changed successfully!",
    returnToLogin: "Return to Login",
    welcome: "Welcome to Chessbitcoin Wallet",
    welcomeSubtitle: "Manage your assets on the Solana blockchain",
    savedWallets: "Your Saved Wallets",
    temporaryWalletsList: "Temporary Wallets",
    copyAddress: "Copy Address",
    viewExplorer: "View on Explorer",
    disconnect: "Disconnect",
    settings: "Settings",
    security: "Security",
    backup: "Backup Wallet",
    exportPrivateKey: "Export Private Key",
    network: "Network",
    transactionHistory: "Transaction History",
    receive: "Receive",
    buy: "Buy",
    stake: "Stake",
    nfts: "NFTs",
    dapps: "DApps",
    connect: "Connect Wallet",
    connected: "Connected",
    connecting: "Connecting...",
    approve: "Approve",
    reject: "Reject",
    confirm: "Confirm",
    cancel: "Cancel",
    close: "Close",
    save: "Save",
    edit: "Edit",
    remove: "Remove",
    search: "Search tokens...",
    filter: "Filter",
    sort: "Sort",
    all: "All",
    hideZeroBalance: "Hide zero balance",
    showAllTokens: "Show all tokens",
    tokenDetails: "Token Details",
    marketCap: "Market Cap",
    volume: "Volume",
    price: "Price",
    change24h: "24h Change",
    supply: "Supply",
    website: "Website",
    twitter: "Twitter",
    telegram: "Telegram",
    docs: "Documentation",
    contract: "Contract",
    addCustomToken: "Add Custom Token",
    customTokenAddress: "Custom Token Address",
    tokenSymbol: "Token Symbol",
    tokenName: "Token Name",
    decimals: "Decimals",
    add: "Add",
    invalidToken: "Invalid token address",
    tokenAdded: "Token added successfully",
    removeToken: "Remove Token",
    areYouSure: "Are you sure?",
    yes: "Yes",
    no: "No",
    gasFee: "Gas Fee",
    priorityFee: "Priority Fee",
    standard: "Standard",
    fast: "Fast",
    fastest: "Fastest",
    custom: "Custom",
    estimatedTime: "Estimated Time",
    seconds: "seconds",
    minutes: "minutes",
    transactionFee: "Transaction Fee",
    total: "Total",
    insufficientBalance: "Insufficient balance",
    tryAgain: "Try Again",
    success: "Success!",
    error: "Error",
    warning: "Warning",
    info: "Information",
    loading: "Loading...",
    refresh: "Refresh",
    back: "Back",
    next: "Next",
    finish: "Finish",
    skip: "Skip",
    tutorial: "Tutorial",
    help: "Help",
    support: "Support",
    feedback: "Feedback",
    rate: "Rate Us",
    share: "Share",
    about: "About",
    version: "Version",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    community: "Community",
    developers: "Developers",
    blog: "Blog",
    announcements: "Announcements",
    receiveTokens: "Receive Tokens",
    shareAddress: "Share this address to receive tokens",
    scanQR: "Scan QR Code",
    shareAddressBtn: "Share Address",
    downloadQR: "Download QR",
    copyLink: "Copy Link",
    shareVia: "Share via",
    whatsapp: "WhatsApp",
    telegramShare: "Telegram",
    emailShare: "Email",
    generateNewAddress: "Generate New Address",
    depositInstructions: "Deposit Instructions",
    depositSol: "Deposit SOL",
    depositTokens: "Deposit Tokens",
    onlySendSol: "Only send SOL and SPL tokens (Solana tokens) to this address",
    doNotSend: "Do NOT send:",
    ethereumTokens: "Ethereum tokens (ERC-20)",
    bitcoin: "Bitcoin (BTC)",
    otherNetworks: "Tokens from other networks",
    networkWarning: "Sending other tokens may result in permanent loss",
    copySuccess: "Address copied to clipboard!",
    qrGenerated: "QR code generated successfully"
  },
  es: {
    appTitle: "Chessbitcoin Wallet",
    mainnet: "Mainnet",
    login: "Iniciar Sesión",
    logout: "Cerrar Sesión",
    temporary: "(Temporal)",
    loginRequired: "Debes iniciar sesión para crear una wallet",
    walletCreated: "¡Wallet creada exitosamente!",
    walletRestored: "¡Wallet restaurada exitosamente!",
    sessionClosed: "Sesión cerrada",
    dataUpdated: "Datos actualizados",
    invalidNetworkUSDT: "⚠️ PELIGRO: ¡Estás intentando enviar USDT de Solana a una dirección de Ethereum!",
    invalidNetworkUSDTDetail: "Esta dirección es para USDT de Ethereum. Tú tienes USDT de Solana (token SPL).",
    invalidNetworkUSDTAction: "Pídele a tu amigo su dirección de USDT de Solana (comienza con 'EPjF...' o similar)",
    invalidNetworkGeneric: "⚠️ Dirección de red inválida",
    validSolanaAddress: "✅ Dirección Solana válida",
    wallet: "💰 Wallet",
    send: "📤 Enviar",
    swap: "🔄 Swap",
    receive: "📥 Recibir",
    wallets: "👛 Wallets",
    totalBalance: "Balance Total",
    temporarySession: "⚠️ Sesión temporal - Inicia sesión para guardar tu wallet",
    updateAll: "🔄 Actualizar Todo",
    updating: "⏳ Actualizando...",
    yourTokens: "Tus Tokens",
    noTokens: "No se encontraron tokens con balance",
    reloadTokens: "🔄 Recargar Tokens",
    walletInfo: "Información de la Wallet",
    address: "Dirección:",
    copy: "📋 Copiar",
    mnemonic: "Mnemonic:",
    showMnemonic: "👁️ Mostrar Mnemonic",
    hideMnemonic: "👁️ Ocultar Mnemonic",
    sendTokens: "Enviar Tokens",
    tokenToSend: "Token a Enviar:",
    recipient: "Destinatario:",
    enterSolanaAddress: "Ingresa la dirección Solana",
    amount: "Cantidad",
    availableBalance: "Balance disponible:",
    max: "MAX",
    send: "🚀 Enviar",
    sending: "⏳ Enviando...",
    yourWallets: "Tus Wallets",
    temporaryWallets: "Wallets Temporales",
    walletsWillBeLost: "⚠️ Las wallets temporales se perderán al recargar la página",
    active: "Activa",
    delete: "Eliminar",
    addNewWallet: "+ Agregar Nueva Wallet",
    createTemporaryWallet: "+ Crear Wallet Temporal",
    manageWallets: "Gestionar Wallets",
    createTemporaryWalletModal: "Crear Wallet Temporal",
    newWallet: "🆕 Nueva Wallet",
    creating: "⏳ Creando...",
    or: "O",
    enterMnemonic: "Ingresa tu frase mnemonic de 12 o 24 palabras",
    restoreWallet: "🔑 Restaurar Wallet",
    restoring: "⏳ Restaurando...",
    loginTitle: "Iniciar Sesión",
    registerTitle: "Registrarse",
    email: "Email:",
    enterEmail: "tu@email.com",
    username: "Usuario:",
    enterUsername: "Nombre de usuario",
    password: "Contraseña:",
    minPassword: "Mínimo 6 caracteres",
    processing: "⏳ Procesando...",
    noAccount: "¿No tienes cuenta?",
    haveAccount: "¿Ya tienes cuenta?",
    signUpHere: "Regístrate aquí",
    loginHere: "Inicia sesión aquí",
    forgotPassword: "¿Olvidaste tu contraseña?",
    resetPassword: "Recuperar Contraseña",
    resetPasswordTitle: "Recupera Tu Contraseña",
    enterEmailForReset: "Ingresa tu email para recuperar tu contraseña",
    sendResetLink: "Enviar Enlace de Recuperación",
    resetLinkSent: "¡Enlace de recuperación enviado a tu email!",
    backToLogin: "Volver al Login",
    resetPasswordModalTitle: "Restablecer Contraseña",
    newPassword: "Nueva Contraseña:",
    confirmPassword: "Confirmar Contraseña:",
    enterNewPassword: "Ingresa nueva contraseña",
    reEnterPassword: "Vuelve a ingresar la contraseña",
    passwordsDontMatch: "Las contraseñas no coinciden",
    resetting: "⏳ Restableciendo...",
    resetPasswordBtn: "Restablecer Contraseña",
    passwordChanged: "¡Contraseña cambiada exitosamente!",
    returnToLogin: "Volver al Login",
    welcome: "Bienvenido a Chessbitcoin Wallet",
    welcomeSubtitle: "Gestiona tus activos en la blockchain de Solana",
    savedWallets: "Tus Wallets Guardadas",
    temporaryWalletsList: "Wallets Temporales",
    copyAddress: "Copiar Dirección",
    viewExplorer: "Ver en Explorer",
    disconnect: "Desconectar",
    settings: "Configuración",
    security: "Seguridad",
    backup: "Respaldar Wallet",
    exportPrivateKey: "Exportar Clave Privada",
    network: "Red",
    transactionHistory: "Historial de Transacciones",
    receive: "Recibir",
    buy: "Comprar",
    stake: "Staking",
    nfts: "NFTs",
    dapps: "DApps",
    connect: "Conectar Wallet",
    connected: "Conectado",
    connecting: "Conectando...",
    approve: "Aprobar",
    reject: "Rechazar",
    confirm: "Confirmar",
    cancel: "Cancelar",
    close: "Cerrar",
    save: "Guardar",
    edit: "Editar",
    remove: "Eliminar",
    search: "Buscar tokens...",
    filter: "Filtrar",
    sort: "Ordenar",
    all: "Todos",
    hideZeroBalance: "Ocultar balance cero",
    showAllTokens: "Mostrar todos los tokens",
    tokenDetails: "Detalles del Token",
    marketCap: "Capitalización",
    volume: "Volumen",
    price: "Precio",
    change24h: "Cambio 24h",
    supply: "Suministro",
    website: "Sitio Web",
    twitter: "Twitter",
    telegram: "Telegram",
    docs: "Documentación",
    contract: "Contrato",
    addCustomToken: "Agregar Token Personalizado",
    customTokenAddress: "Dirección del Token",
    tokenSymbol: "Símbolo del Token",
    tokenName: "Nombre del Token",
    decimals: "Decimales",
    add: "Agregar",
    invalidToken: "Dirección de token inválida",
    tokenAdded: "Token agregado exitosamente",
    removeToken: "Eliminar Token",
    areYouSure: "¿Estás seguro?",
    yes: "Sí",
    no: "No",
    gasFee: "Tarifa de Gas",
    priorityFee: "Tarifa de Prioridad",
    standard: "Estándar",
    fast: "Rápido",
    fastest: "Más Rápido",
    custom: "Personalizado",
    estimatedTime: "Tiempo Estimado",
    seconds: "segundos",
    minutes: "minutos",
    transactionFee: "Tarifa de Transacción",
    total: "Total",
    insufficientBalance: "Balance insuficiente",
    tryAgain: "Intentar Nuevamente",
    success: "¡Éxito!",
    error: "Error",
    warning: "Advertencia",
    info: "Información",
    loading: "Cargando...",
    refresh: "Refrescar",
    back: "Atrás",
    next: "Siguiente",
    finish: "Finalizar",
    skip: "Omitir",
    tutorial: "Tutorial",
    help: "Ayuda",
    support: "Soporte",
    feedback: "Comentarios",
    rate: "Calificarnos",
    share: "Compartir",
    about: "Acerca de",
    version: "Versión",
    privacy: "Política de Privacidad",
    terms: "Términos de Servicio",
    community: "Comunidad",
    developers: "Desarrolladores",
    blog: "Blog",
    announcements: "Anuncios",
    receiveTokens: "Recibir Tokens",
    shareAddress: "Comparte esta dirección para recibir tokens",
    scanQR: "Escanear código QR",
    shareAddressBtn: "Compartir Dirección",
    downloadQR: "Descargar QR",
    copyLink: "Copiar Enlace",
    shareVia: "Compartir vía",
    whatsapp: "WhatsApp",
    telegramShare: "Telegram",
    emailShare: "Email",
    generateNewAddress: "Generar Nueva Dirección",
    depositInstructions: "Instrucciones de Depósito",
    depositSol: "Depositar SOL",
    depositTokens: "Depositar Tokens",
    onlySendSol: "Solo envía SOL y tokens SPL (tokens de Solana) a esta dirección",
    doNotSend: "NO envíes:",
    ethereumTokens: "Tokens de Ethereum (ERC-20)",
    bitcoin: "Bitcoin (BTC)",
    otherNetworks: "Tokens de otras redes",
    networkWarning: "Enviar otros tokens puede resultar en pérdida permanente",
    copySuccess: "¡Dirección copiada al portapapeles!",
    qrGenerated: "Código QR generado exitosamente"
  }
};

const PasswordInput = ({
  value,
  onChange,
  placeholder,
  showPassword,
  setShowPassword,
  disabled = false,
  className = ""
}) => {
  return (
    <div className="password-input-container">
      <div className="input-with-icon">
        <input
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`phantom-input ${className}`}
          disabled={disabled}
          minLength={6}
        />
        <button
          type="button"
          onClick={() => !disabled && setShowPassword(!showPassword)}
          disabled={disabled}
          className="password-toggle"
        >
          {showPassword ? "🙈" : "👁️"}
        </button>
      </div>
    </div>
  );
};

// Componente QR Code
const QRCodeGenerator = ({ address, size = 200 }) => {
  const [qrCode, setQrCode] = useState('');

  useEffect(() => {
    const generateQRCode = () => {
      if (!address) return;
      
      try {
        // Usar un servicio de generación de QR simple
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(address)}`;
        setQrCode(qrUrl);
      } catch (error) {
        console.error('Error generando QR:', error);
      }
    };

    generateQRCode();
  }, [address, size]);

  if (!qrCode) {
    return (
      <div className="qr-code-placeholder">
        <div className="spinner"></div>
        <p>Generando código QR...</p>
      </div>
    );
  }

  return (
    <div className="qr-code-container">
      <img src={qrCode} alt="QR Code" className="qr-code-image" />
    </div>
  );
};

const validateSolanaAddress = (address) => {
  if (!address || typeof address !== 'string') return false;
  if (address.length < 32 || address.length > 44) return false;
  const base58Pattern = /^[1-9A-HJ-NP-Za-km-z]+$/;
  if (!base58Pattern.test(address)) return false;
  return true;
};

const isEthereumAddress = (address) => {
  if (!address || typeof address !== 'string') return false;
  return /^0x[a-fA-F0-9]{40}$/.test(address.trim());
};

const getAddressType = (address) => {
  if (!address || typeof address !== 'string') {
    return { type: 'unknown', token: 'unknown' };
  }
  
  const cleanAddress = address.toLowerCase().trim();
  
  const usdtAddresses = {
    ethereum: [
      '0xdac17f958d2ee523a2206206994597c13d831ec7',
      '0xc2132d05d31c914a87c6611c10748aeb04b58e8f',
      '0x55d398326f99059ff775485246999027b3197955',
      '0x4988a896b1227218e4a686fde5eabdcabd91571f',
    ],
    solana: [
      'es9vfrk8ax3kmy81kwut0v8lw4q4j6ejqg5qje5c',
      'epjfwddymxynodlruql0lkgddrhqdyhccewgyemrs',
      'hz1c1a7m6z7epg7d0aa6n2c2vqkfrwrch7rx7ka0h',
    ]
  };

  if (usdtAddresses.ethereum.includes(cleanAddress)) {
    return { type: 'ethereum', token: 'USDT' };
  }
  
  if (usdtAddresses.solana.includes(cleanAddress)) {
    return { type: 'solana', token: 'USDT' };
  }

  if (isEthereumAddress(address)) {
    return { type: 'ethereum', token: 'unknown' };
  }

  return { type: 'unknown', token: 'unknown' };
};

const hasSolanaUSDT = (userTokens) => {
  if (!userTokens || !Array.isArray(userTokens)) return false;
  return userTokens.some(token => {
    if (!token) return false;
    return token.symbol === 'USDT' || 
           token.name?.toLowerCase().includes('tether') ||
           (token.mint && token.mint.toLowerCase().includes('usdt'));
  });
};

// Componente de Swap integrado directamente
const SwapFormComponent = ({
  swapInputToken,
  setSwapInputToken,
  swapOutputToken,
  setSwapOutputToken,
  swapAmount,
  setSwapAmount,
  swapQuote,
  setSwapQuote,
  swapLoading,
  setSwapLoading,
  quoteLoading,
  setQuoteLoading,
  popularTokens,
  userTokens,
  currentWallet,
  setMessage,
  getBalance,
  getUserTokens,
  prices,
  language,
  user,
  fetchWithTimeout
}) => {
  const t = (key) => translations[language][key] || key;

  const getSwapQuote = async () => {
    if (!swapAmount || parseFloat(swapAmount) <= 0) {
      setMessage('Ingresa una cantidad válida');
      return;
    }

    if (!user) {
      setMessage('Debes iniciar sesión para usar el swap');
      return;
    }

    setQuoteLoading(true);
    setMessage('Obteniendo cotización...');

    try {
      // Calcular el amount en la unidad correcta
      let amountInSmallestUnit = 0;
      
      if (swapInputToken === 'So11111111111111111111111111111111111111112') {
        // SOL tiene 9 decimales
        amountInSmallestUnit = Math.floor(parseFloat(swapAmount) * 1_000_000_000);
      } else {
        // Buscar el token en userTokens para obtener los decimales
        const inputTokenInfo = userTokens.find(t => t.mint === swapInputToken) || 
                              popularTokens.find(t => t.mint === swapInputToken);
        const decimals = inputTokenInfo?.decimals || 6;
        amountInSmallestUnit = Math.floor(parseFloat(swapAmount) * Math.pow(10, decimals));
      }

      const quoteResponse = await fetchWithTimeout('/swap-quote', {
        method: 'POST',
        body: JSON.stringify({
          inputMint: swapInputToken,
          outputMint: swapOutputToken,
          amount: amountInSmallestUnit.toString(),
          slippage: 100
        })
      }, 15000);

      setSwapQuote(quoteResponse.data);
      setMessage('✅ Cotización obtenida exitosamente');
    } catch (error) {
      console.error('❌ Error obteniendo cotización:', error);
      setMessage(`❌ Error obteniendo cotización: ${error.message}`);
    } finally {
      setQuoteLoading(false);
    }
  };

  const executeSwap = async () => {
    if (!swapQuote || !currentWallet || !currentWallet.secretKey) {
      setMessage('Falta información para ejecutar el swap');
      return;
    }

    if (!user) {
      setMessage('Debes iniciar sesión para ejecutar un swap');
      return;
    }

    setSwapLoading(true);
    setMessage('Ejecutando swap...');

    try {
      const swapResponse = await fetchWithTimeout('/swap', {
        method: 'POST',
        body: JSON.stringify({
          secretKey: currentWallet.secretKey,
          quoteResponse: swapQuote,
          provider: swapQuote.provider || 'jupiter-v7-lite'
        })
      }, 30000);

      setMessage(`✅ Swap ejecutado exitosamente! Signature: ${swapResponse.data.signature}`);
      setSwapQuote(null);
      setSwapAmount('');

      // Actualizar balances después del swap
      setTimeout(() => {
        getBalance();
        getUserTokens();
      }, 3000);

    } catch (error) {
      console.error('❌ Error ejecutando swap:', error);
      setMessage(`❌ Error en swap: ${error.message}`);
    } finally {
      setSwapLoading(false);
    }
  };

  const handleReverseTokens = () => {
    const temp = swapInputToken;
    setSwapInputToken(swapOutputToken);
    setSwapOutputToken(temp);
    setSwapQuote(null);
  };

  const calculateSwapOutput = () => {
    if (!swapQuote?.quote || !swapAmount) return null;
    
    try {
      const inputToken = popularTokens.find(t => t.mint === swapInputToken) || 
                        userTokens.find(t => t.mint === swapInputToken);
      const outputToken = popularTokens.find(t => t.mint === swapOutputToken) || 
                         userTokens.find(t => t.mint === swapOutputToken);
      
      if (!inputToken || !outputToken) return null;

      const inputDecimals = inputToken.decimals || 6;
      const outputDecimals = outputToken.decimals || 6;

      const inAmount = Number(swapQuote.quote.inAmount) / Math.pow(10, inputDecimals);
      const outAmount = Number(swapQuote.quote.outAmount) / Math.pow(10, outputDecimals);
      
      const rate = outAmount / inAmount;
      const estimatedOutput = parseFloat(swapAmount) * rate;

      return {
        amount: estimatedOutput.toFixed(6),
        rate: rate.toFixed(6),
        tokenSymbol: outputToken.symbol
      };
    } catch (error) {
      console.error('Error calculando output:', error);
      return null;
    }
  };

  const swapOutputInfo = calculateSwapOutput();

  return (
    <div className="swap-tab">
      <div className="tab-header">
        <h2>🔄 {t('swap')}</h2>
        <div className="tab-subtitle">Swap entre tokens en Solana</div>
      </div>
      
      <div className="swap-container">
        <div className="swap-card">
          <div className="swap-section">
            <div className="form-group">
              <label className="form-label">{t('tokenToSend')}</label>
              <div className="token-select-wrapper">
                <select
                  value={swapInputToken}
                  onChange={(e) => {
                    setSwapInputToken(e.target.value);
                    setSwapQuote(null);
                  }}
                  className="phantom-select"
                >
                  {userTokens.filter(token => token.uiAmount > 0).map(token => (
                    <option key={token.mint} value={token.mint}>
                      {token.symbol} - {token.uiAmount?.toFixed(6) || '0.000000'}
                    </option>
                  ))}
                </select>
                <div className="select-arrow">▼</div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('amount')}</label>
              <div className="amount-input-wrapper">
                <input
                  type="number"
                  value={swapAmount}
                  onChange={(e) => {
                    setSwapAmount(e.target.value);
                    setSwapQuote(null);
                  }}
                  placeholder="0.0"
                  step="0.000001"
                  min="0"
                  className="phantom-input"
                />
                <div className="balance-info">
                  <span>
                    {(() => {
                      const inputToken = userTokens.find(t => t.mint === swapInputToken);
                      if (inputToken) {
                        return `Balance: ${inputToken.uiAmount?.toFixed(6) || '0.000000'}`;
                      }
                      return '';
                    })()}
                  </span>
                  <button
                    type="button"
                    className="max-btn"
                    onClick={() => {
                      const inputToken = userTokens.find(t => t.mint === swapInputToken);
                      if (inputToken) {
                        setSwapAmount(inputToken.uiAmount?.toString() || '0');
                        setSwapQuote(null);
                      }
                    }}
                  >
                    {t('max')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="swap-reverse-container">
            <button
              onClick={handleReverseTokens}
              className="reverse-btn"
              title="Invertir tokens"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 3L21 7L17 11" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 7H21" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 21L3 17L7 13" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 17H3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          <div className="swap-section">
            <div className="form-group">
              <label className="form-label">Token de Salida</label>
              <div className="token-select-wrapper">
                <select
                  value={swapOutputToken}
                  onChange={(e) => {
                    setSwapOutputToken(e.target.value);
                    setSwapQuote(null);
                  }}
                  className="phantom-select"
                >
                  {popularTokens.map(token => (
                    <option key={token.mint} value={token.mint}>
                      {token.symbol}
                    </option>
                  ))}
                </select>
                <div className="select-arrow">▼</div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Recibirás</label>
              <div className="estimated-output">
                {swapQuote && swapOutputInfo ? (
                  <>
                    <div className="output-amount">
                      <span className="amount-value">{swapOutputInfo.amount}</span>
                      <span className="token-symbol">{swapOutputInfo.tokenSymbol}</span>
                    </div>
                    <div className="swap-rate">
                      1 {userTokens.find(t => t.mint === swapInputToken)?.symbol || 'TOKEN'} ≈ {swapOutputInfo.rate} {swapOutputInfo.tokenSymbol}
                    </div>
                  </>
                ) : (
                  <div className="output-placeholder">
                    Ingresa cantidad para ver estimación
                  </div>
                )}
              </div>
            </div>
          </div>

          {!user && (
            <div className="auth-warning">
              <div className="warning-icon">⚠️</div>
              <div className="warning-text">
                Debes <strong>iniciar sesión</strong> para usar la función de swap
              </div>
            </div>
          )}

          {user && (
            <div className="swap-actions">
              {!swapQuote ? (
                <button
                  onClick={getSwapQuote}
                  disabled={quoteLoading || !swapAmount || parseFloat(swapAmount) <= 0 || !user}
                  className="phantom-btn primary swap-btn"
                >
                  {quoteLoading ? (
                    <>
                      <span className="spinner"></span>
                      Obteniendo cotización...
                    </>
                  ) : '📊 Obtener Cotización'}
                </button>
              ) : (
                <>
                  <div className="quote-details">
                    <div className="quote-header">
                      <h4>Detalles del Swap</h4>
                    </div>
                    <div className="quote-info">
                      <div className="quote-row">
                        <span>Proveedor:</span>
                        <span className="provider">{swapQuote.provider}</span>
                      </div>
                      {swapQuote.quote?.priceImpact && (
                        <div className="quote-row">
                          <span>Impacto de precio:</span>
                          <span className={`price-impact ${swapQuote.quote.priceImpact > 0.01 ? 'high' : 'low'}`}>
                            {(swapQuote.quote.priceImpact * 100).toFixed(2)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={executeSwap}
                    disabled={swapLoading || !user}
                    className="phantom-btn primary swap-btn"
                  >
                    {swapLoading ? (
                      <>
                        <span className="spinner"></span>
                        Ejecutando swap...
                      </>
                    ) : '🚀 Ejecutar Swap'}
                  </button>
                  <button
                    onClick={() => setSwapQuote(null)}
                    className="phantom-btn outline cancel-btn"
                  >
                    Cancelar
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente de Receive
const ReceiveTabComponent = ({ currentWallet, language, setMessage }) => {
  const t = (key) => translations[language][key] || key;
  const [showShareOptions, setShowShareOptions] = useState(false);

  if (!currentWallet || !currentWallet.publicKey) {
    return (
      <div className="receive-tab">
        <div className="tab-header">
          <h2>📥 {t('receive')}</h2>
          <div className="tab-subtitle">Selecciona una wallet para recibir tokens</div>
        </div>
      </div>
    );
  }

  const handleCopyAddress = () => {
    copyToClipboard(currentWallet.publicKey, setMessage, t('copySuccess'));
  };

  const handleShareAddress = () => {
    setShowShareOptions(!showShareOptions);
  };

  const handleShareVia = (platform) => {
    const message = `Mi dirección de Solana: ${currentWallet.publicKey}\n\nPuedes enviarme SOL o cualquier token de Solana.`;
    let url = '';
    
    switch(platform) {
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(message)}`;
        break;
      case 'telegram':
        url = `https://t.me/share/url?url=${encodeURIComponent(currentWallet.publicKey)}&text=${encodeURIComponent('Mi dirección de Solana:')}`;
        break;
      case 'email':
        url = `mailto:?subject=Mi dirección de Solana&body=${encodeURIComponent(message)}`;
        break;
    }
    
    if (url) {
      window.open(url, '_blank');
      setShowShareOptions(false);
    }
  };

  const handleDownloadQR = () => {
    if (!currentWallet.publicKey) return;
    
    try {
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(currentWallet.publicKey)}`;
      
      fetch(qrUrl)
        .then(response => response.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `solana-address-${currentWallet.publicKey.substring(0, 8)}.png`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          setMessage('✅ QR descargado exitosamente');
        });
    } catch (error) {
      console.error('Error descargando QR:', error);
      setMessage('❌ Error al descargar QR');
    }
  };

  return (
    <div className="receive-tab">
      <div className="tab-header">
        <h2>📥 {t('receiveTokens')}</h2>
        <div className="tab-subtitle">{t('shareAddress')}</div>
      </div>
      
      <div className="receive-container">
        <div className="receive-card">
          <div className="qr-section">
            <div className="qr-header">
              <h3>📱 {t('scanQR')}</h3>
            </div>
            <div className="qr-code-wrapper">
              <QRCodeGenerator address={currentWallet.publicKey} size={200} />
              <div className="qr-actions">
                <button
                  onClick={handleDownloadQR}
                  className="phantom-btn outline qr-btn"
                >
                  ⬇️ {t('downloadQR')}
                </button>
              </div>
            </div>
          </div>

          <div className="address-section">
            <div className="address-header">
              <h3>📍 {t('address')}</h3>
              <div className="address-actions">
                <button
                  onClick={handleCopyAddress}
                  className="phantom-btn primary copy-btn"
                >
                  📋 {t('copy')}
                </button>
                <button
                  onClick={handleShareAddress}
                  className="phantom-btn outline share-btn"
                >
                  📤 {t('shareAddressBtn')}
                </button>
              </div>
            </div>
            
            <div className="address-display">
              <div className="address-box">
                <code className="address-text">{currentWallet.publicKey}</code>
              </div>
              
              <div className="wallet-info-badge">
                {currentWallet.walletName && (
                  <span className="wallet-name-badge">
                    👛 {currentWallet.walletName}
                  </span>
                )}
                <span className="network-badge">
                  🌐 Solana Mainnet
                </span>
              </div>
            </div>

            {showShareOptions && (
              <div className="share-options">
                <h4>{t('shareVia')}</h4>
                <div className="share-buttons">
                  <button
                    onClick={() => handleShareVia('whatsapp')}
                    className="share-btn whatsapp"
                  >
                    💚 {t('whatsapp')}
                  </button>
                  <button
                    onClick={() => handleShareVia('telegram')}
                    className="share-btn telegram"
                  >
                    💙 {t('telegramShare')}
                  </button>
                  <button
                    onClick={() => handleShareVia('email')}
                    className="share-btn email"
                  >
                    📧 {t('emailShare')}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="instructions-section">
            <h3>📝 {t('depositInstructions')}</h3>
            <div className="instructions-card">
              <div className="instruction-item positive">
                <div className="instruction-icon">✅</div>
                <div className="instruction-content">
                  <strong>{t('depositSol')}:</strong> {t('onlySendSol')}
                </div>
              </div>
              
              <div className="instruction-item negative">
                <div className="instruction-icon">❌</div>
                <div className="instruction-content">
                  <strong>{t('doNotSend')}</strong>
                  <ul className="warning-list">
                    <li>{t('ethereumTokens')}</li>
                    <li>{t('bitcoin')}</li>
                    <li>{t('otherNetworks')}</li>
                  </ul>
                </div>
              </div>
              
              <div className="instruction-item warning">
                <div className="instruction-icon">⚠️</div>
                <div className="instruction-content">
                  <strong>Advertencia:</strong> {t('networkWarning')}
                </div>
              </div>
            </div>
            
            <div className="network-info">
              <div className="network-item">
                <span className="network-label">Red:</span>
                <span className="network-value">Solana Mainnet</span>
              </div>
              <div className="network-item">
                <span className="network-label">Tipo:</span>
                <span className="network-value">Dirección Pública</span>
              </div>
              <div className="network-item">
                <span className="network-label">Formato:</span>
                <span className="network-value">Base58 (44 caracteres)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [wallets, setWallets] = useState([]);
  const [currentWallet, setCurrentWallet] = useState(null);
  const [sendAmount, setSendAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('wallet');
  const [showMnemonic, setShowMnemonic] = useState(false);
  const [showNewWalletModal, setShowNewWalletModal] = useState(false);
  const [mnemonicInput, setMnemonicInput] = useState('');
  const [addressValidation, setAddressValidation] = useState({
    isValid: false,
    message: '',
    isCritical: false,
    details: '',
    action: ''
  });

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({
    username: '',
    email: '',
    password: ''
  });

  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [recoverySuccess, setRecoverySuccess] = useState(false);

  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    token: '',
    email: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState('');
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(null);

  const [swapInputToken, setSwapInputToken] = useState('So11111111111111111111111111111111111111112');
  const [swapOutputToken, setSwapOutputToken] = useState('9EpY5vX7Jwc9z9v6un8HEpRuGWrMvWenVvcM1h1EXpmR');
  const [swapAmount, setSwapAmount] = useState('');
  const [swapQuote, setSwapQuote] = useState(null);
  const [swapLoading, setSwapLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const [sendToken, setSendToken] = useState('SOL');
  const [tokenBalance, setTokenBalance] = useState(0);

  const [language, setLanguage] = useState('es');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const {
    userTokens,
    popularTokens,
    getUserTokens,
    refreshTokenPrices,
    getCSBTCData,
    getWalletData,
    prices,
    loading: tokensLoading
  } = useTokens(currentWallet);

  const t = (key) => translations[language][key] || key;

  const API_BASE_URL = window.location.hostname === 'localhost' 
    ? 'http://localhost:3001'
    : window.location.origin;

  const changeLanguage = (lang) => {
    setLanguage(lang);
  };

  const walletData = getWalletData();
  const csbtcData = getCSBTCData();

  const calculateTotalBalanceUSD = () => {
    let total = walletData.solValueUSD || 0;
    if (userTokens && userTokens.length > 0) {
      userTokens.forEach((token) => {
        if (token.symbol !== 'SOL' && token.valueUSD && token.valueUSD > 0) {
          total += token.valueUSD;
        }
      });
    }
    return total;
  };

  const totalBalanceUSD = calculateTotalBalanceUSD();

  const getToken = () => localStorage.getItem('jwtToken');
  const setToken = (token) => localStorage.setItem('jwtToken', token);
  const removeToken = () => {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('currentWallet');
  };

  const saveCurrentWalletToStorage = (wallet) => {
    if (wallet && user) {
      localStorage.setItem('currentWallet', JSON.stringify(wallet));
    }
  };

  const loadCurrentWalletFromStorage = () => {
    if (!user) return null;
    try {
      const storedWallet = localStorage.getItem('currentWallet');
      if (storedWallet) {
        return JSON.parse(storedWallet);
      }
    } catch (error) {
      console.error('Error loading wallet from storage:', error);
    }
    return null;
  };

  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    };
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const fetchWithTimeout = async (endpoint, options = {}, timeout = 15000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    try {
      const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
      
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers
      };

      const authRequiredEndpoints = [
        '/user/generate-wallet',
        '/user/wallets',
        '/user/wallet/',
        '/send-transaction',
        '/swap-quote',
        '/swap',
        '/verify-token'
      ];

      const isAuthRequired = authRequiredEndpoints.some(authEndpoint => 
        endpoint.includes(authEndpoint)
      );

      if (isAuthRequired) {
        const token = getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        } else {
          throw new Error('Token de autenticación requerido');
        }
      }

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        credentials: 'include',
        headers: headers
      });
      
      clearTimeout(id);
      
      if (!response.ok) {
        const errorText = await response.text();
        
        if (response.status === 401 || response.status === 403) {
          throw new Error('Token de autenticación inválido o expirado');
        }
        
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      return { ok: true, data };
    } catch (error) {
      clearTimeout(id);
      
      if (error.name === 'AbortError') {
        throw new Error('Timeout: La solicitud tardó demasiado');
      }
      
      if (error.message.includes('Failed to fetch')) {
        throw new Error(`No se pudo conectar con el servidor en ${API_BASE_URL}. Verifica que el backend esté corriendo.`);
      }
      
      if (error.message.includes('Token de autenticación')) {
        removeToken();
        setUser(null);
        setCurrentWallet(null);
        setWallets([]);
        setShowAuthModal(true);
      }
      
      throw error;
    }
  };

  const validateRecipientAddress = (address) => {
    if (!address || address.trim() === '') {
      setAddressValidation({
        isValid: false,
        message: '',
        isCritical: false,
        details: '',
        action: ''
      });
      return false;
    }

    const isValidSolana = validateSolanaAddress(address);
    
    if (isValidSolana) {
      setAddressValidation({
        isValid: true,
        message: t('validSolanaAddress'),
        isCritical: false,
        details: '',
        action: ''
      });
      return true;
    }

    const addressType = getAddressType(address);
    
    if (addressType.type === 'ethereum' && (addressType.token === 'USDT' || sendToken === 'USDT')) {
      const userHasSolanaUSDT = hasSolanaUSDT(userTokens);
      
      if (userHasSolanaUSDT || sendToken === 'USDT') {
        setAddressValidation({
          isValid: false,
          message: t('invalidNetworkUSDT'),
          isCritical: true,
          details: t('invalidNetworkUSDTDetail'),
          action: t('invalidNetworkUSDTAction')
        });
        return false;
      }
    }

    if (addressType.type === 'ethereum') {
      setAddressValidation({
        isValid: false,
        message: t('invalidNetworkGeneric'),
        isCritical: false,
        details: 'Esta es una dirección Ethereum. Esta wallet solo soporta Solana.',
        action: 'Usa una dirección Solana válida'
      });
      return false;
    }

    setAddressValidation({
      isValid: false,
      message: '⚠️ Dirección no reconocida',
      isCritical: false,
      details: 'No es una dirección Solana válida',
      action: 'Verifica la dirección e intenta nuevamente'
    });
    return false;
  };

  useEffect(() => {
    const initializeApp = async () => {
      const token = getToken();
      if (token) {
        await verifyAuth();
      } else {
        setUser(null);
        setCurrentWallet(null);
        setWallets([]);
      }
    };
    initializeApp();
  }, []);

  useEffect(() => {
    if (user) {
      const storedWallet = loadCurrentWalletFromStorage();
      if (storedWallet) {
        setCurrentWallet(storedWallet);
        setTimeout(() => {
          getBalance(storedWallet.publicKey);
        }, 100);
      } else {
        setTimeout(() => {
          loadUserWallets();
        }, 100);
      }
    } else {
      setCurrentWallet(null);
      setWallets([]);
    }
  }, [user]);

  useEffect(() => {
    const checkForResetParams = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const email = urlParams.get('email');

      if (token && email) {
        setResetPasswordData(prev => ({
          ...prev,
          token: token,
          email: decodeURIComponent(email)
        }));

        setShowResetPasswordModal(true);
        setShowAuthModal(false);
        setShowPasswordRecovery(false);

        verifyResetToken(token, decodeURIComponent(email));

        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };
    checkForResetParams();
  }, []);

  const verifyResetToken = async (token, email) => {
    try {
      const result = await fetchWithTimeout('/api/auth/verify-reset-token', {
        method: 'POST',
        body: JSON.stringify({ token, email })
      }, 10000);

      if (result.data.valid) {
        setIsTokenValid(true);
        setResetPasswordMessage('✅ Token válido. Ingresa tu nueva contraseña.');
      } else {
        setIsTokenValid(false);
        setResetPasswordMessage('❌ Enlace inválido o expirado. Solicita un nuevo enlace.');
      }
    } catch (error) {
      setIsTokenValid(false);
      setResetPasswordMessage('⚠️ Error de conexión. Intenta nuevamente.');
    }
  };

  const verifyAuth = async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      return;
    }

    try {
      const result = await fetchWithTimeout('/verify-token', {
        headers: getAuthHeaders()
      }, 8000);

      if (result.ok) {
        setUser(result.data.user);
      } else {
        removeToken();
        setUser(null);
        setCurrentWallet(null);
        setWallets([]);
      }
    } catch (error) {
      console.error('❌ Error verificando autenticación:', error);
      setMessage('❌ Error: ' + error.message);
      removeToken();
      setUser(null);
      setCurrentWallet(null);
      setWallets([]);
    }
  };

  const loadUserWallets = async () => {
    if (!user) {
      setWallets([]);
      return;
    }

    try {
      const result = await fetchWithTimeout('/user/wallets', {
        headers: getAuthHeaders()
      }, 10000);

      if (result.ok && result.data.wallets && result.data.wallets.length > 0) {
        const formattedWallets = result.data.wallets.map(wallet => ({
          id: wallet._id || wallet.id,
          publicKey: wallet.publicKey,
          walletName: wallet.walletName,
          secretKey: wallet.secretKey || '',
          mnemonic: wallet.mnemonic || ''
        }));

        setWallets(formattedWallets);

        if (!currentWallet && formattedWallets.length > 0) {
          await selectUserWallet(formattedWallets[0]);
        }
      } else {
        setWallets([]);
        setMessage('No tienes wallets guardadas. Crea una nueva wallet.');
      }
    } catch (error) {
      console.error('❌ Error cargando wallets:', error);
      setMessage('❌ Error: ' + error.message);
    }
  };

  const selectUserWallet = async (wallet) => {
    if (!user) {
      setMessage('❌ Debes iniciar sesión para seleccionar una wallet');
      return;
    }

    try {
      const connectionData = {
        publicKey: wallet.publicKey,
        walletName: wallet.walletName,
        id: wallet.id,
        secretKey: wallet.secretKey || '',
        mnemonic: wallet.mnemonic || ''
      };

      setCurrentWallet(connectionData);
      saveCurrentWalletToStorage(connectionData);

      await getBalance(wallet.publicKey);

      setActiveTab('wallet');
      setMessage(`✅ Wallet ${wallet.walletName} seleccionada`);

    } catch (error) {
      console.error('❌ Error seleccionando wallet:', error);
      setMessage('Error al cargar la wallet');
    }
  };

  const selectWallet = (wallet) => {
    setCurrentWallet(wallet);
    getBalance(wallet.publicKey);
    setActiveTab('wallet');
  };

  const handleRegister = async () => {
    setAuthLoading(true);
    setMessage('');
    try {
      const result = await fetchWithTimeout('/register', {
        method: 'POST',
        body: JSON.stringify(authForm)
      }, 15000);

      setToken(result.data.token);
      setUser(result.data.user);
      setShowAuthModal(false);
      setAuthForm({ username: '', email: '', password: '' });
      setMessage('✅ Registro exitoso!');

      setTimeout(() => {
        loadUserWallets();
      }, 500);

    } catch (error) {
      setMessage('❌ Error: ' + error.message);
    }
    setAuthLoading(false);
  };

  const handleLogin = async () => {
    setAuthLoading(true);
    setMessage('');
    try {
      const result = await fetchWithTimeout('/login', {
        method: 'POST',
        body: JSON.stringify({
          username: authForm.username,
          password: authForm.password
        })
      }, 15000);

      setToken(result.data.token);
      setUser(result.data.user);
      setShowAuthModal(false);
      setAuthForm({ username: '', email: '', password: '' });
      setMessage('✅ Login exitoso! Cargando wallets...');

      setTimeout(() => {
        loadUserWallets();
      }, 500);

    } catch (error) {
      setMessage('❌ Error: ' + error.message);
    }
    setAuthLoading(false);
  };

  const handlePasswordRecovery = async () => {
    if (!recoveryEmail.trim()) {
      setRecoveryMessage('Por favor ingresa tu email');
      return;
    }

    setRecoveryLoading(true);
    setRecoveryMessage('');

    try {
      const result = await fetchWithTimeout('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: recoveryEmail.trim() }),
      }, 15000);

      setRecoverySuccess(true);
      setRecoveryMessage(result.data.message || t('resetLinkSent'));
    } catch (error) {
      setRecoverySuccess(true);
      setRecoveryMessage('Si el email existe, se ha enviado un enlace de recuperación.');
    }
    setRecoveryLoading(false);
  };

  const handleResetPasswordSubmit = async () => {
    if (!resetPasswordData.newPassword || !resetPasswordData.confirmPassword) {
      setResetPasswordMessage('Por favor completa todos los campos');
      return;
    }

    if (resetPasswordData.newPassword.length < 6) {
      setResetPasswordMessage('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      setResetPasswordMessage(t('passwordsDontMatch'));
      return;
    }

    setResetPasswordLoading(true);
    setResetPasswordMessage('');

    try {
      const result = await fetchWithTimeout('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({
          token: resetPasswordData.token,
          email: resetPasswordData.email,
          newPassword: resetPasswordData.newPassword
        })
      }, 15000);

      setResetPasswordSuccess(true);
      setResetPasswordMessage(t('passwordChanged'));

      setResetPasswordData(prev => ({
        ...prev,
        newPassword: '',
        confirmPassword: ''
      }));

      setTimeout(() => {
        setShowResetPasswordModal(false);
        setShowAuthModal(true);
        setResetPasswordSuccess(false);
        setResetPasswordMessage('');
        setIsTokenValid(null);
        setMessage('✅ Contraseña cambiada. Ahora puedes iniciar sesión.');
      }, 3000);
    } catch (error) {
      setResetPasswordMessage('❌ Error: ' + error.message);
      setResetPasswordSuccess(false);
    }
    setResetPasswordLoading(false);
  };

  const handleLogout = () => {
    removeToken();
    setUser(null);
    setCurrentWallet(null);
    setWallets([]);
    setMessage(t('sessionClosed'));
  };

  const generateWallet = async () => {
    if (!user) {
      setShowAuthModal(true);
      setMessage(t('loginRequired'));
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const result = await fetchWithTimeout('/user/generate-wallet', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({})
      }, 15000);

      const newWallet = {
        id: result.data.wallet.id,
        publicKey: result.data.wallet.publicKey,
        walletName: result.data.wallet.walletName,
        secretKey: result.data.wallet.secretKey || '',
        mnemonic: result.data.wallet.mnemonic || '',
        createdAt: result.data.wallet.createdAt
      };

      setWallets(prev => [newWallet, ...prev]);
      setCurrentWallet(newWallet);
      saveCurrentWalletToStorage(newWallet);
      setShowNewWalletModal(false);
      setMessage(t('walletCreated'));

      setTimeout(() => {
        getBalance(newWallet.publicKey);
      }, 1000);

    } catch (error) {
      setMessage('❌ Error: ' + error.message);
    }
    setLoading(false);
  };

  const restoreWallet = async () => {
    if (!mnemonicInput.trim()) {
      setMessage('Por favor ingresa el mnemonic');
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const result = await fetchWithTimeout('/restore-wallet', {
        method: 'POST',
        body: JSON.stringify({ mnemonic: mnemonicInput.trim() }),
      }, 15000);

      const walletData = {
        publicKey: result.data.publicKey,
        secretKey: result.data.secretKey || '',
        mnemonic: result.data.mnemonic || '',
        walletName: 'Wallet Restaurada'
      };

      if (user) {
        try {
          const saveResult = await fetchWithTimeout('/user/generate-wallet', {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ 
              mnemonic: mnemonicInput.trim(),
              walletName: 'Wallet Restaurada'
            })
          }, 15000);

          const savedWallet = {
            id: saveResult.data.wallet.id,
            publicKey: saveResult.data.wallet.publicKey,
            walletName: saveResult.data.wallet.walletName,
            secretKey: saveResult.data.wallet.secretKey || '',
            mnemonic: saveResult.data.wallet.mnemonic || ''
          };

          setWallets(prev => [savedWallet, ...prev]);
          setCurrentWallet(savedWallet);
          saveCurrentWalletToStorage(savedWallet);
          setMessage(t('walletRestored'));
        } catch (saveError) {
          setCurrentWallet(walletData);
          setWallets(prev => [walletData, ...prev]);
          saveCurrentWalletToStorage(walletData);
          setMessage('✅ Wallet restaurada (no se pudo guardar en perfil)');
        }
      } else {
        setCurrentWallet(walletData);
        setWallets(prev => [walletData, ...prev]);
        setMessage(t('walletRestored') + ' (sesión temporal)');
      }

      setShowNewWalletModal(false);
      setMnemonicInput('');

      setTimeout(() => getBalance(walletData.publicKey), 1000);

    } catch (error) {
      setMessage('❌ Error: ' + error.message);
    }
    setLoading(false);
  };

  const deleteWallet = async (walletId, event) => {
    event.stopPropagation();
    if (!user) {
      setMessage('❌ Debes estar autenticado para eliminar wallets');
      return;
    }

    if (window.confirm('¿Estás seguro de que quieres eliminar esta wallet?')) {
      try {
        await fetchWithTimeout(`/user/wallet/${walletId}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        }, 10000);

        setWallets(prev => prev.filter(w => w.id !== walletId));
        if (currentWallet && currentWallet.id === walletId) {
          setCurrentWallet(null);
          localStorage.removeItem('currentWallet');
        }
        setMessage('✅ Wallet eliminada');
      } catch (error) {
        setMessage('❌ Error eliminando wallet');
      }
    }
  };

  const getBalance = async (publicKey = null) => {
    const targetPublicKey = publicKey || (currentWallet && currentWallet.publicKey);
    if (!targetPublicKey) return;

    try {
      await getUserTokens(targetPublicKey);
    } catch (error) {
      setMessage('⚠️ Error al obtener balance: ' + error.message);
    }
  };

  const refreshAllDataWithDebounce = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    setLoading(true);
    setMessage('Actualizando...');
    
    try {
      await Promise.all([
        getBalance(),
        refreshTokenPrices()
      ]);
      setMessage(t('dataUpdated'));
    } catch (error) {
      setMessage('⚠️ Error al actualizar algunos datos');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setIsRefreshing(false);
      }, 3000);
    }
  };

  const sendTransaction = async () => {
    if (!currentWallet || !sendAmount || !recipient) {
      setMessage('Por favor completa todos los campos');
      return;
    }

    if (!currentWallet.secretKey || currentWallet.secretKey.trim() === '') {
      setMessage('❌ Error: No se encontró la clave privada de la wallet. Intenta restaurar la wallet nuevamente.');
      return;
    }

    if (!validateRecipientAddress(recipient)) {
      setMessage(addressValidation.message);
      return;
    }

    if (parseFloat(sendAmount) > tokenBalance) {
      setMessage('❌ Cantidad excede el balance disponible');
      return;
    }

    if (addressValidation.isCritical) {
      setMessage('❌ NO ENVIES: ' + addressValidation.message);
      return;
    }

    setLoading(true);
    setMessage('Procesando transacción...');

    try {
      let transactionData = {
        secretKey: currentWallet.secretKey.trim(),
        toPublicKey: recipient.trim(),
        amount: sendAmount.trim(),
      };

      if (sendToken !== 'SOL') {
        const selectedToken = userTokens.find(token => token.symbol === sendToken);
        if (selectedToken) {
          transactionData.tokenMint = selectedToken.mint;
          transactionData.decimals = selectedToken.decimals || 6;
        }
      }

      const result = await fetchWithTimeout('/send-transaction', {
        method: 'POST',
        body: JSON.stringify(transactionData),
      }, 30000);

      setMessage(`✅ Transacción de ${sendToken} exitosa! Signature: ${result.data.signature}`);
      setSendAmount('');
      setRecipient('');
      setAddressValidation({ 
        isValid: false, 
        message: '',
        isCritical: false,
        details: '',
        action: ''
      });

      setTimeout(() => {
        getBalance(currentWallet.publicKey);
        if (sendToken !== 'SOL') {
          getUserTokens();
        }
      }, 3000);

    } catch (error) {
      console.error('❌ Error completo en sendTransaction:', error);
      if (error.message.includes('insufficient funds')) {
        setMessage('❌ Fondos insuficientes. Necesitas SOL para pagar las fees de gas.');
      } else if (error.message.includes('secretKey')) {
        setMessage('❌ Error con la clave privada. Verifica que la wallet tenga una clave privada válida.');
      } else {
        setMessage('❌ Error: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sendToken === 'SOL') {
      setTokenBalance(walletData.solBalance || 0);
    } else {
      const selectedToken = userTokens.find(token => token.symbol === sendToken);
      setTokenBalance(selectedToken?.uiAmount || 0);
    }
  }, [sendToken, userTokens, walletData.solBalance]);

  useEffect(() => {
    if (user) {
      const timer = setTimeout(() => {
        loadUserWallets();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setWallets([]);
    }
  }, [user]);

  useEffect(() => {
    if (currentWallet) {
      getBalance();
      const interval = setInterval(() => {
        getBalance();
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [currentWallet]);

  useEffect(() => {
    if (recipient && recipient.trim() !== '') {
      validateRecipientAddress(recipient);
    } else {
      setAddressValidation({ 
        isValid: false, 
        message: '',
        isCritical: false,
        details: '',
        action: ''
      });
    }
  }, [recipient, sendToken, userTokens]);

  const renderResetPasswordModal = () => {
    if (!showResetPasswordModal) return null;

    return (
      <div className="modal-overlay">
        <div className="modal auth-modal">
          <h2>{t('resetPasswordModalTitle')}</h2>
          <div className="auth-form">
            {isTokenValid === null ? (
              <div className="verifying-token">
                <p>Verificando enlace...</p>
              </div>
            ) : !isTokenValid ? (
              <div className="invalid-token">
                <div className="error-icon">✕</div>
                <h3>Enlace Inválido</h3>
                <p className="error-message">{resetPasswordMessage}</p>
                <button
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setShowPasswordRecovery(true);
                  }}
                  className="phantom-btn primary"
                >
                  Solicitar Nuevo Enlace
                </button>
                <button
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setShowAuthModal(true);
                  }}
                  className="phantom-btn outline"
                >
                  {t('backToLogin')}
                </button>
              </div>
            ) : resetPasswordSuccess ? (
              <div className="success-screen">
                <div className="success-icon">✓</div>
                <h3>¡Contraseña Cambiada!</h3>
                <p className="success-message">{resetPasswordMessage}</p>
                <p>Redirigiendo al login...</p>
              </div>
            ) : (
              <>
                <p className="reset-info">
                  Establece una nueva contraseña para: <strong>{resetPasswordData.email}</strong>
                </p>
                <div className="form-group">
                  <label>{t('newPassword')}</label>
                  <PasswordInput
                    value={resetPasswordData.newPassword}
                    onChange={(e) => setResetPasswordData(prev => ({
                      ...prev,
                      newPassword: e.target.value
                    }))}
                    placeholder={t('enterNewPassword')}
                    showPassword={showNewPassword}
                    setShowPassword={setShowNewPassword}
                    disabled={resetPasswordLoading}
                  />
                </div>
                <div className="form-group">
                  <label>{t('confirmPassword')}</label>
                  <PasswordInput
                    value={resetPasswordData.confirmPassword}
                    onChange={(e) => setResetPasswordData(prev => ({
                      ...prev,
                      confirmPassword: e.target.value
                    }))}
                    placeholder={t('reEnterPassword')}
                    showPassword={showConfirmPassword}
                    setShowPassword={setShowConfirmPassword}
                    disabled={resetPasswordLoading}
                  />
                </div>
                {resetPasswordMessage && (
                  <div className={`reset-password-message ${resetPasswordMessage.includes('✅') || resetPasswordMessage.includes('válido') ? 'success' : 'error'}`}>
                    {resetPasswordMessage}
                  </div>
                )}
                <button
                  onClick={handleResetPasswordSubmit}
                  disabled={resetPasswordLoading ||
                    !resetPasswordData.newPassword ||
                    !resetPasswordData.confirmPassword ||
                    resetPasswordData.newPassword !== resetPasswordData.confirmPassword ||
                    resetPasswordData.newPassword.length < 6}
                  className="phantom-btn primary reset-btn"
                >
                  {resetPasswordLoading ? t('resetting') : t('resetPasswordBtn')}
                </button>
                <button
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setShowAuthModal(true);
                  }}
                  className="phantom-btn outline"
                  disabled={resetPasswordLoading}
                >
                  {t('backToLogin')}
                </button>
              </>
            )}
          </div>
          <button
            className="modal-close"
            onClick={() => {
              setShowResetPasswordModal(false);
              setResetPasswordData({
                token: '',
                email: '',
                newPassword: '',
                confirmPassword: ''
              });
              setResetPasswordMessage('');
              setResetPasswordSuccess(false);
              setIsTokenValid(null);
              setShowNewPassword(false);
              setShowConfirmPassword(false);
            }}
          >
            ✕
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="app phantom-style">
      <header className="phantom-header">
        <div className="header-left">
          <div className="logo">
            <img src="/icon2.png" alt="Chessbitcoin Wallet" className="logo-icon" />
            <span className="logo-text">{t('appTitle')}</span>
          </div>
        </div>
        
        <button 
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>

        <div className="header-center">
          <div className="network-indicator">
            <span className="network-dot"></span>
            {t('mainnet')}
          </div>
        </div>
        
        <div className={`header-right ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="language-switcher">
            <button
              className={`lang-btn ${language === 'es' ? 'active' : ''}`}
              onClick={() => changeLanguage('es')}
            >
              🇪🇸 ES
            </button>
            <button
              className={`lang-btn ${language === 'en' ? 'active' : ''}`}
              onClick={() => changeLanguage('en')}
            >
              🇺🇸 EN
            </button>
          </div>
          
          {currentWallet && (
            <div className="current-wallet-info">
              <div className="wallet-address-display">
                {formatAddress(currentWallet.publicKey)}
                {currentWallet.walletName && ` - ${currentWallet.walletName}`}
                {!user && <span className="temporary-badge">{t('temporary')}</span>}
              </div>
            </div>
          )}
          
          {user ? (
            <div className="user-info">
              <div className="user-avatar">
                {user.username.charAt(0).toUpperCase()}
              </div>
              <div className="user-details">
                <span className="username">@{user.username}</span>
                <button className="logout-btn" onClick={handleLogout}>
                  {t('logout')}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="connect-btn"
              onClick={() => setShowAuthModal(true)}
            >
              {t('login')}
            </button>
          )}
        </div>
      </header>

      <main className="phantom-main">
        {message && (
          <div className={`phantom-message ${message.includes('❌') ? 'error' : message.includes('⚠️') ? 'warning' : 'success'}`}>
            <div className="message-content">
              {message}
              <button className="message-close" onClick={() => setMessage('')}>✕</button>
            </div>
          </div>
        )}

        {!currentWallet ? (
          <div className="welcome-screen">
            <div className="welcome-content">
              <div className="welcome-header">
                <h1>{t('welcome')}</h1>
                <p className="welcome-subtitle">{t('welcomeSubtitle')}</p>
              </div>
              
              <div className="welcome-actions">
                <button
                  className="phantom-btn primary"
                  onClick={() => setShowNewWalletModal(true)}
                >
                  {user ? t('addNewWallet') : t('createTemporaryWallet')}
                </button>
                
                {wallets.length > 0 && (
                  <div className="saved-wallets">
                    <h3>{user ? t('savedWallets') : t('temporaryWalletsList')}</h3>
                    <div className="wallet-list">
                      {wallets.map(wallet => (
                        <div
                          key={wallet.id || wallet.publicKey}
                          className="wallet-item"
                          onClick={() => user ? selectUserWallet(wallet) : selectWallet(wallet)}
                        >
                          <div className="wallet-item-content">
                            <div className="wallet-icon">👛</div>
                            <div className="wallet-info">
                              <div className="wallet-address">
                                {formatAddress(wallet.publicKey)}
                              </div>
                              {wallet.walletName && (
                                <div className="wallet-name">{wallet.walletName}</div>
                              )}
                              {!user && <div className="temporary-badge">{t('temporary')}</div>}
                            </div>
                          </div>
                          {user && wallet.id && (
                            <button
                              className="delete-btn"
                              onClick={(e) => deleteWallet(wallet.id, e)}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="dashboard">
            <nav className="phantom-nav">
              <button
                className={`nav-btn ${activeTab === 'wallet' ? 'active' : ''}`}
                onClick={() => setActiveTab('wallet')}
              >
                <span className="nav-icon">💰</span>
                <span className="nav-text">{t('wallet')}</span>
              </button>
              <button
                className={`nav-btn ${activeTab === 'send' ? 'active' : ''}`}
                onClick={() => setActiveTab('send')}
              >
                <span className="nav-icon">📤</span>
                <span className="nav-text">{t('send')}</span>
              </button>
              <button
                className={`nav-btn ${activeTab === 'receive' ? 'active' : ''}`}
                onClick={() => setActiveTab('receive')}
              >
                <span className="nav-icon">📥</span>
                <span className="nav-text">{t('receive')}</span>
              </button>
              <button
                className={`nav-btn ${activeTab === 'swap' ? 'active' : ''}`}
                onClick={() => setActiveTab('swap')}
              >
                <span className="nav-icon">🔄</span>
                <span className="nav-text">{t('swap')}</span>
              </button>
              <button
                className={`nav-btn ${activeTab === 'wallets' ? 'active' : ''}`}
                onClick={() => setActiveTab('wallets')}
              >
                <span className="nav-icon">👛</span>
                <span className="nav-text">{t('wallets')}</span>
              </button>
            </nav>

            <div className="tab-content">
              {activeTab === 'wallet' && (
                <div className="wallet-tab">
                  <div className="balance-card">
                    <div className="balance-header">
                      <h2>{t('totalBalance')}</h2>
                      <button
                        className="refresh-btn"
                        onClick={refreshAllDataWithDebounce}
                        disabled={loading || tokensLoading || isRefreshing}
                        title={t('refresh')}
                      >
                        {loading || tokensLoading || isRefreshing ? (
                          <span className="spinner"></span>
                        ) : (
                          '↻'
                        )}
                      </button>
                    </div>
                    <div className="balance-amount">
                      <div className="balance-usd">${totalBalanceUSD.toFixed(2)} USD</div>
                      {walletData.solBalance && (
                        <div className="balance-sol">
                          {walletData.solBalance.toFixed(6)} SOL
                        </div>
                      )}
                    </div>
                    {!user && (
                      <div className="temporary-warning">
                        ⚠️ {t('temporarySession')}
                      </div>
                    )}
                  </div>

                  {userTokens.length > 0 ? (
                    <div className="tokens-section">
                      <div className="tokens-header">
                        <h3>{t('yourTokens')} ({userTokens.length})</h3>
                        <button
                          className="reload-tokens-btn"
                          onClick={() => getUserTokens()}
                          disabled={tokensLoading}
                        >
                          {tokensLoading ? t('updating') : '↻'}
                        </button>
                      </div>
                      <div className="tokens-list">
                        {userTokens.map(token => (
                          <div key={token.mint} className="token-item">
                            <div className="token-info">
                              <div className="token-symbol-row">
                                <span className="token-symbol">{token.symbol}</span>
                                {token.priceChange24h !== 0 && (
                                  <span className={`token-change ${token.priceChange24h >= 0 ? 'positive' : 'negative'}`}>
                                    {token.priceChange24h >= 0 ? '↗' : '↘'}
                                    {Math.abs(token.priceChange24h).toFixed(2)}%
                                  </span>
                                )}
                              </div>
                              <span className="token-name">{token.name}</span>
                            </div>
                            <div className="token-balance">
                              <div className="token-amount">
                                {token.uiAmount?.toFixed(6) || '0.000000'}
                              </div>
                              {token.valueUSD > 0 && (
                                <div className="token-value">
                                  ${token.valueUSD.toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="tokens-section">
                      <h3>{t('yourTokens')}</h3>
                      <p className="no-tokens">{t('noTokens')}</p>
                      <button
                        className="phantom-btn outline"
                        onClick={() => getUserTokens()}
                        disabled={tokensLoading}
                      >
                        {tokensLoading ? t('updating') : t('reloadTokens')}
                      </button>
                    </div>
                  )}

                  <div className="wallet-details">
                    <h3>{t('walletInfo')}</h3>
                    <div className="detail-card">
                      <div className="detail-row">
                        <label>{t('address')}</label>
                        <div className="address-container">
                          <span className="address-value">{currentWallet.publicKey}</span>
                          <button
                            onClick={() => copyToClipboard(currentWallet.publicKey, setMessage)}
                            className="icon-btn"
                            title={t('copy')}
                          >
                            {t('copy')}
                          </button>
                        </div>
                      </div>
                      <div className="detail-row">
                        <label>{t('mnemonic')}</label>
                        <div className="mnemonic-section">
                          {showMnemonic ? (
                            <div className="mnemonic-container">
                              <span className="mnemonic-text">{currentWallet.mnemonic}</span>
                              <div className="mnemonic-actions">
                                <button
                                  onClick={() => copyToClipboard(currentWallet.mnemonic, setMessage)}
                                  className="icon-btn"
                                  title={t('copy')}
                                >
                                  {t('copy')}
                                </button>
                                <button
                                  className="phantom-btn outline"
                                  onClick={() => setShowMnemonic(false)}
                                >
                                  {t('hideMnemonic')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              className="phantom-btn outline"
                              onClick={() => setShowMnemonic(true)}
                            >
                              {t('showMnemonic')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'send' && (
                <div className="send-tab">
                  <div className="tab-header">
                    <h2>📤 {t('sendTokens')}</h2>
                    <div className="tab-subtitle">Enviar tokens a cualquier dirección Solana</div>
                  </div>
                  
                  <div className="send-card">
                    <div className="form-group">
                      <label className="form-label">{t('tokenToSend')}</label>
                      <div className="token-select-wrapper">
                        <select
                          value={sendToken}
                          onChange={(e) => setSendToken(e.target.value)}
                          className="phantom-select"
                        >
                          <option value="SOL">
                            SOL - {walletData.solBalance?.toFixed(6) || '0.000000'}
                          </option>
                          {userTokens.filter(token => token.uiAmount > 0).map(token => (
                            <option key={token.mint} value={token.symbol}>
                              {token.symbol} - {token.uiAmount?.toFixed(6) || '0.000000'}
                            </option>
                          ))}
                        </select>
                        <div className="select-arrow">▼</div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('recipient')}</label>
                      <div className="recipient-input-wrapper">
                        <input
                          type="text"
                          value={recipient}
                          onChange={(e) => setRecipient(e.target.value)}
                          placeholder={t('enterSolanaAddress')}
                          className="phantom-input"
                        />
                        {recipient && recipient.trim() !== '' && addressValidation.message && (
                          <div className={`address-validation ${addressValidation.isCritical ? 'critical' : addressValidation.isValid ? 'valid' : 'invalid'}`}>
                            <div className="validation-header">
                              <strong>{addressValidation.message}</strong>
                            </div>
                            {addressValidation.details && (
                              <div className="validation-details">
                                {addressValidation.details}
                              </div>
                            )}
                            {addressValidation.action && (
                              <div className="validation-action">
                                <strong>Acción recomendada:</strong> {addressValidation.action}
                              </div>
                            )}
                            {addressValidation.isCritical && (
                              <div className="critical-warning">
                                ⚠️ <strong>LOS FONDOS SE PERDERÍAN PERMANENTEMENTE</strong>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">{t('amount')}</label>
                      <div className="amount-input-wrapper">
                        <input
                          type="number"
                          value={sendAmount}
                          onChange={(e) => setSendAmount(e.target.value)}
                          placeholder="0.0"
                          step="0.000001"
                          min="0"
                          max={tokenBalance}
                          className="phantom-input"
                        />
                        <div className="balance-info">
                          <span>
                            {t('availableBalance')} {tokenBalance.toFixed(6)} {sendToken}
                          </span>
                          <button
                            type="button"
                            className="max-btn"
                            onClick={() => setSendAmount(tokenBalance.toString())}
                          >
                            {t('max')}
                          </button>
                        </div>

                        {sendToken === 'SOL' && prices.solPrice > 0 && sendAmount && (
                          <div className="usd-equivalent">
                            ≈ ${(sendAmount * prices.solPrice).toFixed(2)} USD
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={sendTransaction}
                      disabled={loading || !sendAmount || !recipient || 
                               parseFloat(sendAmount) > tokenBalance || 
                               !addressValidation.isValid ||
                               addressValidation.isCritical ||
                               !currentWallet?.secretKey}
                      className="phantom-btn primary send-btn"
                    >
                      {loading ? (
                        <>
                          <span className="spinner"></span>
                          {t('sending')}
                        </>
                      ) : `🚀 ${t('send')} ${sendToken}`}
                    </button>
                    {!currentWallet?.secretKey && (
                      <div className="warning-message">
                        ⚠️ Esta wallet no tiene clave privada. Puede que necesites restaurarla nuevamente.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'receive' && (
                <ReceiveTabComponent
                  currentWallet={currentWallet}
                  language={language}
                  setMessage={setMessage}
                />
              )}

              {activeTab === 'swap' && (
                <SwapFormComponent
                  swapInputToken={swapInputToken}
                  setSwapInputToken={setSwapInputToken}
                  swapOutputToken={swapOutputToken}
                  setSwapOutputToken={setSwapOutputToken}
                  swapAmount={swapAmount}
                  setSwapAmount={setSwapAmount}
                  swapQuote={swapQuote}
                  setSwapQuote={setSwapQuote}
                  swapLoading={swapLoading}
                  setSwapLoading={setSwapLoading}
                  quoteLoading={quoteLoading}
                  setQuoteLoading={setQuoteLoading}
                  popularTokens={popularTokens}
                  userTokens={userTokens}
                  currentWallet={currentWallet}
                  setMessage={setMessage}
                  getBalance={getBalance}
                  getUserTokens={getUserTokens}
                  prices={prices}
                  language={language}
                  user={user}
                  fetchWithTimeout={fetchWithTimeout}
                />
              )}

              {activeTab === 'wallets' && (
                <div className="wallets-tab">
                  <div className="tab-header">
                    <h2>👛 {user ? t('yourWallets') : t('temporaryWallets')}</h2>
                    {!user && (
                      <div className="tab-subtitle warning">
                        ⚠️ {t('walletsWillBeLost')}
                      </div>
                    )}
                  </div>
                  
                  <div className="wallets-list">
                    {wallets.map(wallet => (
                      <div
                        key={wallet.id || wallet.publicKey}
                        className={`wallet-card ${currentWallet?.publicKey === wallet.publicKey ? 'active' : ''}`}
                        onClick={() => user ? selectUserWallet(wallet) : selectWallet(wallet)}
                      >
                        <div className="wallet-card-content">
                          <div className="wallet-icon">👛</div>
                          <div className="wallet-info">
                            <div className="wallet-address">
                              {formatAddress(wallet.publicKey)}
                            </div>
                            {wallet.walletName && (
                              <div className="wallet-name">{wallet.walletName}</div>
                            )}
                            {!user && <div className="temporary-badge">{t('temporary')}</div>}
                            {currentWallet?.publicKey === wallet.publicKey && (
                              <div className="active-badge">{t('active')}</div>
                            )}
                          </div>
                          {user && wallet.id && (
                            <button
                              className="delete-card-btn"
                              onClick={(e) => deleteWallet(wallet.id, e)}
                              title={t('delete')}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="add-wallet-section">
                    <button
                      className="phantom-btn primary"
                      onClick={() => setShowNewWalletModal(true)}
                    >
                      {user ? t('addNewWallet') : t('createTemporaryWallet')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {showNewWalletModal && (
          <div className="modal-overlay">
            <div className="modal wallet-modal">
              <div className="modal-header">
                <h2>{user ? t('manageWallets') : t('createTemporaryWalletModal')}</h2>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowNewWalletModal(false);
                    setMnemonicInput('');
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div className="modal-content">
                <div className="wallet-creation-options">
                  <div className="creation-option">
                    <div className="option-icon">🆕</div>
                    <div className="option-content">
                      <h3>Crear Nueva Wallet</h3>
                      <p>Genera una nueva wallet con mnemonic</p>
                      <button
                        className="phantom-btn primary"
                        onClick={generateWallet}
                        disabled={loading}
                      >
                        {loading ? t('creating') : t('newWallet')}
                      </button>
                    </div>
                  </div>
                  
                  <div className="divider">
                    <span>{t('or')}</span>
                  </div>
                  
                  <div className="creation-option">
                    <div className="option-icon">🔑</div>
                    <div className="option-content">
                      <h3>Restaurar Wallet</h3>
                      <p>Usa tu frase mnemonic existente</p>
                      <div className="restore-section">
                        <textarea
                          value={mnemonicInput}
                          onChange={(e) => setMnemonicInput(e.target.value)}
                          placeholder={t('enterMnemonic')}
                          rows="3"
                          className="phantom-textarea"
                        />
                        <button
                          onClick={restoreWallet}
                          disabled={loading || !mnemonicInput.trim()}
                          className="phantom-btn secondary"
                        >
                          {loading ? t('restoring') : t('restoreWallet')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {!user && (
                  <div className="temporary-warning">
                    ⚠️ {t('walletsWillBeLost')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showAuthModal && (
          <div className="modal-overlay">
            <div className="modal auth-modal">
              <div className="modal-header">
                <h2>{authMode === 'login' ? t('loginTitle') : t('registerTitle')}</h2>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowAuthModal(false);
                    setAuthForm({ username: '', email: '', password: '' });
                    setShowLoginPassword(false);
                    setShowRegisterPassword(false);
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div className="auth-form">
                {authMode === 'register' && (
                  <div className="form-group">
                    <label>{t('email')}</label>
                    <input
                      type="email"
                      value={authForm.email}
                      onChange={(e) => setAuthForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder={t('enterEmail')}
                      className="phantom-input"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>{t('username')}</label>
                  <input
                    type="text"
                    value={authForm.username}
                    onChange={(e) => setAuthForm(prev => ({ ...prev, username: e.target.value }))}
                    placeholder={t('enterUsername')}
                    className="phantom-input"
                  />
                </div>
                <div className="form-group">
                  <label>{t('password')}</label>
                  <PasswordInput
                    value={authForm.password}
                    onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder={t('minPassword')}
                    showPassword={authMode === 'login' ? showLoginPassword : showRegisterPassword}
                    setShowPassword={authMode === 'login' ? setShowLoginPassword : setShowRegisterPassword}
                  />
                </div>
                <button
                  onClick={authMode === 'login' ? handleLogin : handleRegister}
                  disabled={authLoading || !authForm.username || !authForm.password || (authMode === 'register' && !authForm.email)}
                  className="phantom-btn primary auth-submit"
                >
                  {authLoading ? (
                    <>
                      <span className="spinner"></span>
                      {t('processing')}
                    </>
                  ) : (authMode === 'login' ? t('login') : t('registerTitle'))}
                </button>
                
                {authMode === 'login' && (
                  <div className="forgot-password-link">
                    <button
                      className="link-btn"
                      onClick={() => {
                        setShowAuthModal(false);
                        setShowPasswordRecovery(true);
                      }}
                    >
                      {t('forgotPassword')}
                    </button>
                  </div>
                )}
                
                <div className="auth-switch">
                  <span>
                    {authMode === 'login' ? t('noAccount') : t('haveAccount')}
                  </span>
                  <button
                    className="link-btn"
                    onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                  >
                    {authMode === 'login' ? t('signUpHere') : t('loginHere')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showPasswordRecovery && (
          <div className="modal-overlay">
            <div className="modal auth-modal">
              <div className="modal-header">
                <h2>{t('resetPasswordTitle')}</h2>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowPasswordRecovery(false);
                    setRecoveryEmail('');
                    setRecoveryMessage('');
                    setRecoverySuccess(false);
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div className="auth-form">
                {!recoverySuccess ? (
                  <>
                    <p className="recovery-description">{t('enterEmailForReset')}</p>
                    <div className="form-group">
                      <label>{t('email')}</label>
                      <input
                        type="email"
                        value={recoveryEmail}
                        onChange={(e) => setRecoveryEmail(e.target.value)}
                        placeholder={t('enterEmail')}
                        className="phantom-input"
                        disabled={recoveryLoading}
                      />
                    </div>
                    {recoveryMessage && (
                      <div className={`recovery-message ${recoverySuccess ? 'success' : 'error'}`}>
                        {recoveryMessage}
                      </div>
                    )}
                    <button
                      onClick={handlePasswordRecovery}
                      disabled={recoveryLoading || !recoveryEmail.trim()}
                      className="phantom-btn primary recovery-btn"
                    >
                      {recoveryLoading ? (
                        <>
                          <span className="spinner"></span>
                          {t('processing')}
                        </>
                      ) : t('sendResetLink')}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="success-icon">✓</div>
                    <p className="success-message">{recoveryMessage}</p>
                    <button
                      onClick={() => {
                        setShowPasswordRecovery(false);
                        setRecoveryEmail('');
                        setRecoveryMessage('');
                        setRecoverySuccess(false);
                        setShowAuthModal(true);
                      }}
                      className="phantom-btn primary recovery-btn"
                    >
                      {t('backToLogin')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {renderResetPasswordModal()}
      </main>
    </div>
  );
};

export default App;
